// API Configuration for Fleet Portal
// Handles different environments (development, staging, production)

// Get environment variables safely in browser
const getEnvVar = (name: string, defaultValue: string = '') => {
  try {
    return ((import.meta as any).env?.[name] as string) || defaultValue
  } catch {
    return defaultValue
  }
}

// Detect environment
const isProduction = () => {
  try {
    return (import.meta as any).env?.MODE === 'production'
  } catch {
    return window.location.hostname !== 'localhost'
  }
}

// Per-request debug logging is silenced in production builds
const apiLog: (...args: any[]) => void = isProduction() ? () => {} : (...args) => console.log(...args)

export const API_CONFIG = {
  // Base URL configuration based on environment
  BASE_URL: getEnvVar('VITE_API_BASE_URL') || 
           (isProduction() 
             ? '/api'  // Production: relative URL (same domain)
             : 'http://localhost:8123/api'), // Development: existing Fleet API with auth

  // Balise Management backend base URL (proxied through /api/bm)
  BM_BASE_URL: getEnvVar('VITE_BM_API_BASE_URL') ||
           (isProduction()
             ? '/api/bm'
             : '/api/bm'),
  
  // Timeout configuration
  TIMEOUT: parseInt(getEnvVar('VITE_API_TIMEOUT', '10000')),
  MUTATION_TIMEOUT: parseInt(getEnvVar('VITE_API_MUTATION_TIMEOUT', '30000')),
  
  // Endpoints
  ENDPOINTS: {
    VEHICLES: '/balises',
    TRAJECTORY: '/trajectory',
    TIMELINE: '/timeline',
    ALERTS: '/alerts',
    HEALTH: '/health',
    DEBUG_TRAJECTORY: '/debug-trajectory'
  },
  
  // Default parameters
  DEFAULTS: {
    TIME_RANGE_HOURS: 8760, // 1 year
    MAX_TRAJECTORY_POINTS: 500,
    REFRESH_INTERVAL: 30000 // 30 seconds
  }
}

// Utility function to build API URLs
export const buildApiUrl = (endpoint: string, params?: Record<string, string | number>): string => {
  // Handle endpoints that already start with /api or are absolute URLs
  let url = endpoint.startsWith('/api') || endpoint.startsWith('http') 
    ? endpoint 
    : `${API_CONFIG.BASE_URL}${endpoint}`
  
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value))
    })
    url += `?${searchParams.toString()}`
  }
  
  return url
}

// Enhanced API client with authentication support
export const apiClient = {
  get: async (endpoint: string, params?: Record<string, string | number>, options?: { headers?: Record<string, string>, signal?: AbortSignal }) => {
    const url = buildApiUrl(endpoint, params)
    apiLog(`🌐 GET Request: ${url}`)
    
    // Get auth token from localStorage
    const token = localStorage.getItem('fleet_access_token')
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options?.headers
        },
        signal: options?.signal || AbortSignal.timeout(API_CONFIG.TIMEOUT)
      })
      
      if (!response.ok) {
        const errorData = await response.text()
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorData}`)
      }
      
      const data = await response.json()
      apiLog(`✅ GET Response: ${url} (${JSON.stringify(data).length} bytes)`)
      return { data, status: response.status }
      
    } catch (error) {
      console.error(`❌ GET Error: ${url}`, error)
      throw error
    }
  },

  post: async (endpoint: string, body?: any, options?: { headers?: Record<string, string>, signal?: AbortSignal }) => {
    const url = buildApiUrl(endpoint)
    apiLog(`🌐 POST Request: ${url}`)
    
    // Get auth token from localStorage
    const token = localStorage.getItem('fleet_access_token')
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options?.headers
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: options?.signal || AbortSignal.timeout(API_CONFIG.MUTATION_TIMEOUT)
      })
      
      if (!response.ok) {
        const errorData = await response.text()
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        
        try {
          const errorJson = JSON.parse(errorData)
          errorMessage = errorJson.message || errorJson.error || errorMessage
        } catch {
          errorMessage = errorData || errorMessage
        }
        
        const error = new Error(errorMessage) as any
        error.response = { status: response.status, data: errorData }
        throw error
      }
      
      const data = await response.json()
      apiLog(`✅ POST Response: ${url} (${JSON.stringify(data).length} bytes)`)
      return { data, status: response.status }
      
    } catch (error) {
      console.error(`❌ POST Error: ${url}`, error)
      throw error
    }
  },

  put: async (endpoint: string, body?: any, options?: { headers?: Record<string, string>, signal?: AbortSignal }) => {
    const url = buildApiUrl(endpoint)
    apiLog(`🔄 PUT Request: ${url}`)
    
    // Get auth token from localStorage
    const token = localStorage.getItem('fleet_access_token')
    
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options?.headers
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: options?.signal || AbortSignal.timeout(API_CONFIG.MUTATION_TIMEOUT)
      })
      
      if (!response.ok) {
        const errorData = await response.text()
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        
        try {
          const errorJson = JSON.parse(errorData)
          errorMessage = errorJson.message || errorJson.error || errorMessage
        } catch {
          errorMessage = errorData || errorMessage
        }
        
        const error = new Error(errorMessage) as any
        error.response = { status: response.status, data: errorData }
        throw error
      }
      
      const data = await response.json()
      apiLog(`✅ PUT Response: ${url} (${JSON.stringify(data).length} bytes)`)
      return { data, status: response.status }
      
    } catch (error) {
      console.error(`❌ PUT Error: ${url}`, error)
      throw error
    }
  },

  patch: async (endpoint: string, body?: any, options?: { headers?: Record<string, string>, signal?: AbortSignal }) => {
    const url = buildApiUrl(endpoint)
    apiLog(`🔄 PATCH Request: ${url}`)
    const token = localStorage.getItem('fleet_access_token')

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options?.headers
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: options?.signal || AbortSignal.timeout(API_CONFIG.MUTATION_TIMEOUT)
      })

      if (!response.ok) {
        const errorData = await response.text()
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        try {
          const errorJson = JSON.parse(errorData)
          errorMessage = errorJson.message || errorJson.error || errorMessage
        } catch {
          errorMessage = errorData || errorMessage
        }
        const error = new Error(errorMessage) as any
        error.response = { status: response.status, data: errorData }
        throw error
      }

      const data = await response.json()
      apiLog(`✅ PATCH Response: ${url} (${JSON.stringify(data).length} bytes)`)
      return { data, status: response.status }
    } catch (error) {
      console.error(`❌ PATCH Error: ${url}`, error)
      throw error
    }
  },

  delete: async (endpoint: string, options?: { headers?: Record<string, string>, signal?: AbortSignal }) => {
    const url = buildApiUrl(endpoint)
    apiLog(`🗑️ DELETE Request: ${url}`)
    
    // Get auth token from localStorage
    const token = localStorage.getItem('fleet_access_token')
    
    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options?.headers
        },
        signal: options?.signal || AbortSignal.timeout(API_CONFIG.MUTATION_TIMEOUT)
      })
      
      if (!response.ok) {
        const errorData = await response.text()
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        
        try {
          const errorJson = JSON.parse(errorData)
          errorMessage = errorJson.message || errorJson.error || errorMessage
        } catch {
          errorMessage = errorData || errorMessage
        }
        
        const error = new Error(errorMessage) as any
        error.response = { status: response.status, data: errorData }
        throw error
      }
      
      const data = await response.json()
      apiLog(`✅ DELETE Response: ${url} (${JSON.stringify(data).length} bytes)`)
      return { data, status: response.status }
      
    } catch (error) {
      console.error(`❌ DELETE Error: ${url}`, error)
      throw error
    }
  }
}

// Environment-specific configurations
export const ENVIRONMENT_CONFIG = {
  development: {
    API_BASE_URL: 'http://localhost:8123/api',
    GOOGLE_MAPS_API_KEY: 'AIzaSyAQU_IbHOrrHwF0mKMYaqcwU0vrSTcd9qE',
    DEBUG_MODE: true
  },
  
  staging: {
    API_BASE_URL: 'https://staging-fleet-api.your-domain.com/api',
    GOOGLE_MAPS_API_KEY: 'AIzaSyAQU_IbHOrrHwF0mKMYaqcwU0vrSTcd9qE',
    DEBUG_MODE: true
  },
  
  production: {
    API_BASE_URL: '/api', // Relative URL for production
    GOOGLE_MAPS_API_KEY: getEnvVar('VITE_GOOGLE_MAPS_API_KEY', 'AIzaSyAQU_IbHOrrHwF0mKMYaqcwU0vrSTcd9qE'),
    DEBUG_MODE: false
  }
}

// Get current environment configuration
export const getCurrentConfig = () => {
  if (isProduction()) {
    return ENVIRONMENT_CONFIG.production
  }
  return ENVIRONMENT_CONFIG.development
}

// Deployment-ready API URLs
export const API_URLS = {
  VEHICLES: () => buildApiUrl(API_CONFIG.ENDPOINTS.VEHICLES),
  TRAJECTORY: (vehicleId: string, hours?: number) => 
    buildApiUrl(`${API_CONFIG.ENDPOINTS.TRAJECTORY}/${vehicleId}`, { 
      hours: hours || API_CONFIG.DEFAULTS.TIME_RANGE_HOURS 
    }),
  TIMELINE: (vehicleId: string, hours?: number) => 
    buildApiUrl(`${API_CONFIG.ENDPOINTS.TIMELINE}/${vehicleId}`, { 
      hours: hours || API_CONFIG.DEFAULTS.TIME_RANGE_HOURS 
    }),
  ALERTS: () => buildApiUrl(API_CONFIG.ENDPOINTS.ALERTS),
  HEALTH: () => buildApiUrl(API_CONFIG.ENDPOINTS.HEALTH)
}

apiLog('🔧 API Configuration:', {
  environment: isProduction() ? 'production' : 'development',
  baseUrl: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  config: getCurrentConfig()
})
